import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { message } from 'antd';
import { createConversation } from 'services/api/refund';
import { useGetConversationById, useSetConversation } from 'hooks/business/lruConversation';
import { useConversationRead } from 'hooks/business/useConversationRead';

/** 本功能的渠道固定是 TikTok US */
const CHANNEL = 'TIKTOK_US' as const;

export interface ResolveSpsConversationParams {
  /** 订单号 —— 接口文档保证有值，用它兜底定位会话 */
  orderId: string;
  externalStoreId: string;
  /** ⚠️ 官方明细不返回买家信息（由 trade 服务补齐，补不到为 null）；为空时无法定位会话 */
  externalBuyerId?: string | null;
  buyerNick?: string | null;
  region: General.UpperCaseRegion;
}

export interface SpsConversation {
  conversationId: string;
  buyerNick: string;
}

/**
 * 「查看会话」—— 与退单「联系买家」（`views/Refund/components/useContactBuyer`）**同一套机制**：
 *
 *   `createConversation`（后端按 externalBuyerId + externalStoreId + orderId 建/取会话，
 *   没有会话会建一个静默会话）
 *   → 写入会话 LRU 缓存 → 标记已读
 *   → 交给调用方用 `ConversationModal` 展示
 *
 * ⚠️ **刻意不用 `searchType`（会话搜索）**：搜索只能「查已有会话」，查不到就没了；
 * `createConversation` 才是「按订单/买家拿到会话」，退单联系买家、评价分析等入口统一走这条。
 *
 * ⚠️ 也不要换成 `useOpenConversation`：它只把会话设为「当前会话」（jotai 状态），
 * 前提是**已经在「会话」页面**，从数据中心调用不会有任何跳转/弹窗（已踩过：有请求但页面没反应）。
 */
export const useOpenSpsChat = () => {
  const { t } = useTranslate();
  const readConv = useConversationRead();
  const setConversation = useSetConversation();
  const getConversationById = useGetConversationById();

  return useMemoizedFn(
    async (params: ResolveSpsConversationParams): Promise<SpsConversation | null> => {
      const { orderId, externalStoreId, externalBuyerId, buyerNick, region } = params;

      // createConversation 的 externalBuyerId 是**必填**（退单那边来自退单行，一定有值）；
      // 我们的明细接口不保证有值，缺失时没法定位买家，直接提示。
      if (!externalBuyerId) {
        message.error(t('未找到该订单对应的会话'));
        return null;
      }

      try {
        const conversation = await createConversation({
          region,
          channel: CHANNEL,
          externalStoreId,
          externalBuyerId,
          buyerNick: buyerNick || undefined,
          orderId,
          sourceAction: 'SEARCH_ORDER',
        });

        if (!conversation?.conversationId) {
          message.error(t('未找到该订单对应的会话'));
          return null;
        }

        // 与退单一致：写进会话 LRU 缓存并标记已读，避免打开后又显示未读
        setConversation(conversation.conversationId, conversation);
        const cached = getConversationById(conversation.conversationId);
        if (cached) {
          readConv(cached);
        }

        return {
          conversationId: conversation.conversationId,
          buyerNick: conversation.buyerNick || buyerNick || '',
        };
      } catch (err) {
        message.error(t('phrase.获取数据失败，请稍后再试'));
        return null;
      }
    },
  );
};

export default useOpenSpsChat;
